import {GitBranchCheckIcon} from '@github-ui/pacer/CustomIcon'
import {RefSelector} from '@github-ui/ref-selector'
import {ReposSelector} from '@github-ui/repos-selector'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {CopilotIcon, GitBranchIcon} from '@primer/octicons-react'
import {IconButton, SkeletonBox} from '@primer/react'
import {useCallback} from 'react'

import {convertChatRepoToTaskRepository} from '../../hooks/use-chat-input'
import type {TaskRepository} from '../../utils/copilot-chat-types'
import {copilotLocalStorage} from '../../utils/copilot-local-storage'
import {CustomAgentSelector} from './CustomAgentSelector'
import {IconButtonSelector} from './IconButtonSelector'
import styles from './RepoBranchAgentSelector.module.css'

type RepoBranchAgentPickerProps = {
  selectedRepo: TaskRepository | undefined
  selectedBranch: string | undefined
  selectedCustomAgent: string | undefined

  onRepoSelect: (repo: TaskRepository | undefined) => void
  onBranchSelect: (branch: string | undefined) => void
  onCustomAgentSelect: (agent: string | undefined) => void

  agentSelectionEnabled?: boolean
}

export const RepoBranchAgentPicker = ({
  selectedRepo,
  selectedBranch,
  selectedCustomAgent,
  onRepoSelect,
  onBranchSelect,
  onCustomAgentSelect,
  agentSelectionEnabled = true,
}: RepoBranchAgentPickerProps) => {
  const onLoad = useCallback(
    (repositories: TaskRepository[]) => {
      // If no repo is selected, check for lastUsedRepo.
      // If no lastUsedRepo is found and there is at least one enabled repo, select the first enabled repo
      if (!selectedRepo) {
        const lastUsedRepo = copilotLocalStorage.getLastUsedRepository()
        if (lastUsedRepo) {
          const convertedRepo = convertChatRepoToTaskRepository(lastUsedRepo)
          onRepoSelect(convertedRepo)
          onBranchSelect(convertedRepo?.defaultBranch)
        } else {
          const firstEnabledRepo = repositories.find(repo => repo.enabled)
          if (firstEnabledRepo) {
            onRepoSelect(firstEnabledRepo)
            onBranchSelect(firstEnabledRepo.defaultBranch)
          }
        }
      }
    },
    [onRepoSelect, onBranchSelect, selectedRepo],
  )

  return (
    <div className={styles.container}>
      <ReposSelector<TaskRepository>
        repositoryLoader={queryForRepos}
        selectionVariant="single"
        selectAllOption={false}
        onSelect={repo => {
          if (repo) {
            onRepoSelect(repo)
            onBranchSelect(repo.defaultBranch)
          }
        }}
        currentSelection={selectedRepo}
        width="medium"
        additionalButtonProps={{
          'aria-label': `Repository: ${selectedRepo?.nameWithOwner || 'Select repository'}`,
        }}
        onLoad={onLoad}
        loadingElement={<SkeletonBox width="150px" height="32px" />}
      />
      <>
        {!selectedRepo ? (
          <IconButton icon={GitBranchIcon} aria-label="Loading branches…" disabled />
        ) : (
          <RefSelector
            // `cacheKey` is something consumers of this component usually get from a key derived from a current repository in the app payload
            // In our case, it's a static string since this page is not aware of any kind of current repository
            cacheKey="repo-branch-agent-selector"
            canCreate={false}
            currentCommitish={selectedBranch ?? selectedRepo.defaultBranch}
            defaultBranch={selectedRepo.defaultBranch}
            owner={selectedRepo.owner}
            repo={selectedRepo.nameOnly}
            types={['branch']}
            selectedRefType="branch"
            closeOnSelect
            hideShowAll
            actionListHeadingText="Select a branch"
            onSelectItem={onBranchSelect}
            renderAnchor={props => (
              <IconButtonSelector
                {...props}
                icon={GitBranchIcon}
                selectedIcon={GitBranchCheckIcon}
                selected={selectedBranch !== selectedRepo.defaultBranch}
                aria-label={`Branch: ${selectedBranch || 'Select branch'}`}
                aria-labelledby={undefined}
              />
            )}
          />
        )}
      </>

      {agentSelectionEnabled &&
        (!selectedRepo ? (
          <IconButton icon={CopilotIcon} aria-label="Loading agents…" disabled />
        ) : (
          <CustomAgentSelector
            nwo={selectedRepo.nameWithOwner}
            branch={selectedBranch}
            selectedAgent={selectedCustomAgent}
            onSelect={onCustomAgentSelect}
          />
        ))}
    </div>
  )
}

const COPILOT_REPOSITORIES_API_ENDPOINT = '/copilot/agent-sessions/repositories'

async function queryForRepos(query: string = '', defaultOwner?: string): Promise<TaskRepository[]> {
  const url = new URL(COPILOT_REPOSITORIES_API_ENDPOINT, window.location.origin)

  // Add query parameter if present
  if (query.trim()) {
    url.searchParams.set('q', query.trim())
  }

  if (defaultOwner) {
    url.searchParams.set('defaultOwner', defaultOwner)
  }

  const response = await verifiedFetchJSON(url.href)

  if (response.ok) {
    const {repositories} = (await response.json()) as {repositories: TaskRepository[]}
    return repositories.map(repo => {
      return {
        id: repo.id,
        nameWithOwner: repo.nameWithOwner,
        enabled: true,
        name: repo.nameWithOwner,
        owner: repo.owner,
        nameOnly: repo.name,
        defaultBranch: repo.defaultBranch,
        visibility: repo.visibility,
      }
    })
  }
  return []
}

try{ RepoBranchAgentPicker.displayName ||= 'RepoBranchAgentPicker' } catch {}